import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ConnectionsApi } from '@/services/api-connections';
import { ConnectionPartnersApi } from '@/services/api-connection-partners';
import { useStore } from 'vuex';

export const useConnectionsStore = defineStore('connections', () => {
  const store = useStore();

  // STATE
  const STATUS = {
    PENDING: 'Pending',
    ENABLED: 'Active',
    ENABLING: 'Enabling',
    DISABLING: 'Disabling',
    DISABLED: 'Disabled',
  };

  const categories = [
    {
      id: 'dutyofcare',
      label: 'Duty of Care',
      text: 'duty of care',
    },
    {
      id: 'expense',
      label: 'Expenses',
      text: 'travel expense',
    },
    {
      id: 'sustain',
      label: 'Sustainability',
      text: 'sustainability',
    },
    {
      id: 'bi',
      label: 'Business Intelligence',
      text: 'business intelligence',
    },
    {
      id: 'itinerary',
      label: 'Itinerary',
      text: 'itinerary',
    },
    {
      id: 'suppliers',
      label: 'Suppliers',
      text: 'supplier',
    },
    {
      id: 'tmc',
      label: 'TMCs',
      text: 'TMC',
    },
    {
      id: 'traveldocs',
      label: 'Travel Documents',
      text: 'travel documents',
    },
  ];
  const partners = ref([
    {
      machine_name: 'expensify_booking',
      name: 'Expensify',
      description:
        'Expensify is designed to help you get your business expenses done in real time and quickly while on the go. Users can book, report, and reconcile business travel in just a few clicks.',
      logo: require('@images/connections/logo-expensify.svg'),
      url: 'https://use.expensify.com/traxo-promo',
      category: 'expense',
      featured: true,
      featured_logo: require('@images/connections/logotype-expensify.svg'),
      featured_foreground: '#30373B',
      featured_background: '#04D47C',
    },
    {
      machine_name: 'isos_traxo',
      name: 'International SOS',
      description:
        'International SOS delivers extensive medical and travel security services, offering global support with expert advice, emergency assistance, and personalized health and safety solutions. Their proactive approach ensures travelers’ well-being worldwide.',
      logo: require('@images/connections/logo-isos.svg'),
      url: 'https://info.traxo.com/traxo-partner-internationalsos',
      category: 'dutyofcare',
      featured: true,
      featured_logo: require('@images/connections/logotype-isos.svg'),
      featured_foreground: '#fff',
      featured_background: '#324593',
    },
    {
      machine_name: 'crisis24_traxo',
      name: 'Crisis24',
      description:
        'Crisis24 supports travel management teams and provides travelers with news alerts and updates about threats and safety issues around the globe.',
      logo: require('@images/connections/logo-crisis24.svg'),
      url: 'https://info.traxo.com/traxo-partner-crisis24',
      category: 'dutyofcare',
      featured: false,
    },
    {
      machine_name: 'emburse_traxo',
      name: 'Emburse',
      description:
        'Emburse offers a variety of business travel and expense management solutions for businesses around the world.',
      logo: require('@images/connections/logo-embursego.svg'),
      url: 'https://www.traxo.com/partners#expense',
      category: 'expense',
      featured: false,
    },
    {
      machine_name: 'embursego_traxo',
      name: 'Emburse Go',
      description:
        'Consolidate everything your travelers need—pre-trip planning resources, reservation management, and company travel information—in one convenient app that drives travel program compliance, adoption, and engagement.',
      logo: require('@images/connections/logo-embursego.svg'),
      url: 'https://www.traxo.com/partners#expense',
      category: 'itinerary',
      featured: false,
    },
    {
      machine_name: 'everbridge_traxo',
      name: 'Everbridge',
      description:
        'Everbridge offers comprehensive solutions for managing critical events, ensuring safety with industry-leading actionable risk intelligence.',
      logo: require('@images/connections/logo-everbridge.svg'),
      url: 'https://info.traxo.com/everbridge',
      category: 'dutyofcare',
      featured: false,
    },
    {
      machine_name: 'globalguardian_traxo',
      name: 'Global Guardian',
      description:
        'Global Guardian provides security services and emergency response in the United States and 100+ other countries.',
      logo: require('@images/connections/logo-globalguardian.svg'),
      url: 'https://www.traxo.com/partners#doc',
      category: 'dutyofcare',
      featured: false,
    },
    {
      machine_name: 'healix_traxo',
      name: 'Healix',
      description:
        'From mishaps to disasters, the Healix team can help your organization mitigate risk for all employees.',
      logo: require('@images/connections/logo-healix.svg'),
      url: 'https://info.traxo.com/traxo-partner-healix',
      category: 'dutyofcare',
      featured: false,
    },
    {
      machine_name: 'travelbank_traxo',
      name: 'TravelBank',
      description:
        'TravelBank provides fast, flexible travel and expense management for your business in an all-in-one solution.',
      logo: require('@images/connections/logo-travelbank.svg'),
      url: 'https://info.traxo.com/travelbank',
      category: 'expense',
      featured: false,
    },
    {
      machine_name: 'safetyiq_traxo',
      name: 'SafetyIQ by JESI',
      description:
        'SafetyIQ is a cutting-edge safety technology solution addressing the critical need to protect employees in high-risk and remote environments.',
      logo: require('@images/connections/logo-safetyiq.svg'),
      url: 'https://www.traxo.com/partners#doc',
      category: 'dutyofcare',
      featured: false,
    },
    {
      machine_name: 'zoho_booking',
      name: 'Zoho',
      description:
        'Zoho acts as a single repository to bring your business activities together, and streamline your process, policy, and people in one platform.',
      logo: require('@images/connections/logo-zoho.svg'),
      url: 'https://www.traxo.com/partners#expense',
      category: 'expense',
      featured: false,
    },
    {
      machine_name: 'elements_traxo',
      name: 'Elements',
      description:
        'Elements offers an AI-powered tool that helps corporations optimize resources, cut operational costs, and reduce Scope 3 emissions from employee activities like business travel, commuting, and remote work.',
      logo: require('@images/connections/logo-elements.svg'),
      url: 'https://info.traxo.com/traxo-partner-elements',
      category: 'bi',
      featured: false,
    },
    {
      machine_name: 'alertmedia_traxo',
      name: 'AlertMedia',
      description:
        'AlertMedia offers companies reliable emergency communication to ensure safety and swift responses for all travelers.',
      logo: require('@images/connections/logo-alertmedia.svg'),
      url: 'https://info.traxo.com/traxo-partner-alert-media',
      category: 'dutyofcare',
      featured: false,
    },
    {
      machine_name: 'aloindex_traxo',
      name: 'Alō Index',
      description:
        'Alō Index is a corporate travel hotel ESG platform that enables buyers to make informed sourcing decisions using accurate, validated E, S, and G data.',
      logo: require('@images/connections/logo-aloindex.svg'),
      url: 'https://info.traxo.com/traxo-partner-alo-index',
      category: 'sustain',
      featured: false,
    },
    // {
    //  machine_name: 'delta_traxo',
    //  name: 'Delta Business',
    //  description: 'Delta is the preferred airline for business travel. Corporate customers can integrate Delta Business channel bookings with their Traxo account.',
    //  logo: require('@images/connections/logo-delta.svg'),
    //  url: 'https://info.traxo.com/traxo-partner-delta',
    //  category: 'suppliers',
    //  featured: false,
    // },
    // {
    //  machine_name: 'united_traxo',
    //  name: 'United for Business',
    //  description: 'No matter how big (or small) your company is, United offers travel solutions made just for you. Corporate customers can integrate United channel bookings with their Traxo account.',
    //  logo: require('@images/connections/logo-united.svg'),
    //  url: 'https://info.traxo.com/united',
    //  category: 'suppliers',
    //  featured: false,
    // },
    // {
    //   machine_name: '1cp_traxo',
    //   name: '1-CP',
    //   description:
    //     '1-CP is a procurement solution that streamlines B2B purchasing and procurement processes for businesses of all sizes.',
    //   logo: require('@images/integrations/logo-1cp.svg'),
    //   url: 'https://www.traxo.com/partners#bi',
    //   category: 'bi',
    //   featured: false,
    // },
    // {
    //   machine_name: 'cibtvisas_traxo',
    //   name: 'CIBT Visas',
    //   description:
    //     'CIBT Visas is a leading global provider of immigration and visa services for corporations and individuals with 60+ offices in 27 countries.',
    //   logo: require('@images/integrations/logo-cibtvisas.svg'),
    //   url: 'https://www.traxo.com/partners#docs',
    //   category: 'traveldocs',
    //   featured: false,
    // },
    // {
    //   machine_name: 'aloindex_traxo',
    //   name: 'Alō Index',
    //   description:
    //     'Alō Index is a corporate travel hotel ESG platform that enables buyers to make informed sourcing decisions using accurate, validated E, S, and G data.',
    //   logo: require('@images/integrations/logo-aloindex.svg'),
    //   url: 'https://info.traxo.com/traxo-partner-alo-index',
    //   category: 'sustain',
    //   featured: false,
    // },
  ]);

  const connectionPartners = ref([]);
  const connections = ref([]);

  // GETTERS
  const enrichedPartners = computed(() => {
    return partners.value
      .map((item) => {
        const partner = connectionPartners.value?.find((partner) => partner.machine_name === item.machine_name);
        const connection = connections.value?.find((connection) => connection.app_id === partner?.id);

        return {
          ...item,
          id: partner?.id,
          status: connection?.status,
        };
      })
      .filter((item) => item.id)
      .sort((a, b) => {
        // First, sort by featured status (featured items first)
        if (a.featured !== b.featured) {
          return b.featured - a.featured;
        }
        // Then sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
  });

  const availableCategories = computed(() => {
    const usedCategoryIds = new Set(partners.value.map((app) => app.category));
    return categories.filter((category) => usedCategoryIds.has(category.id));
  });

  const enabledCount = computed(() => {
    return enrichedPartners.value.filter((item) => item.status === STATUS.ENABLED).length;
  });

  const getConnectionByAppId = computed(() => (id) => {
    return connections.value.find((integration) => integration.app_id === id) ?? {};
  });

  const getPartnersByName = computed(() => (name) => {
    const n = name.toLowerCase();
    return enrichedPartners.value.filter((partner) => partner.name.toLowerCase().includes(n));
  });

  const getPartnersByCategory = computed(() => (category) => {
    return enrichedPartners.value.filter((partner) => partner.category === category);
  });

  const getPartnersByStatus = computed(() => (status) => {
    return enrichedPartners.value.filter((partner) => partner.status === status);
  });

  // ACTIONS
  async function fetchPartners() {
    try {
      const response = await ConnectionPartnersApi.index();
      connectionPartners.value = Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Failed to fetch connection partners', error);
    }
  }

  async function fetchConnections() {
    try {
      const response = await ConnectionsApi.index();
      connections.value = Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Failed to fetch connections', error);
    }
  }

  async function createConnection(app_id) {
    try {
      const response = await ConnectionsApi.store({ app_id: app_id });
      if (response) {
        connections.value.push(response);

        // TODO: Temporary flash message until this page is truly
        //  self-serve when it will switch to a modal for config
        store.dispatch('notifications/create', {
          type: 'flash',
          message: 'Connection requested. Check your inbox for further instructions.',
        });
      }
    } catch (error) {
      console.error('Failed to create a connection', error);
    }
  }

  async function updateConnection(id, data) {
    try {
      const index = connections.value.findIndex((item) => item.id === id);
      const response = await ConnectionsApi.update(id, data);
      if (index !== -1 && response) {
        connections.value[index] = response;

        // TODO: Temporary flash message until this page is truly
        //  self-serve when it will switch to a modal for config
        let msgPrefix = 'Connection requested.';
        if (data.status === STATUS.DISABLED) {
          msgPrefix = 'Disconnect requested.';
        }

        store.dispatch('notifications/create', {
          type: 'flash',
          message: `${msgPrefix} Check your inbox for further instructions.`,
        });
      }
    } catch (error) {
      console.error('Failed to update a connection', error);
    }
  }

  /**
   * Resets the store state back to initial values
   */
  function $reset() {
    connections.value = [];
    connectionPartners.value = [];
  }

  return {
    //State
    STATUS,
    categories,
    availableCategories,
    partners,
    connectionPartners,
    connections,

    //Getters
    enrichedPartners,
    getPartnersByName,
    getPartnersByCategory,
    getPartnersByStatus,
    getConnectionByAppId,
    enabledCount,

    // Actions
    fetchPartners,
    fetchConnections,
    createConnection,
    updateConnection,
    $reset,
  };
});
